/**
 * Form state, sync, submit, and change-guard wiring for MassagerDetailModal.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import type { UseFormReturn } from 'react-hook-form';
import type { MassagerClinicLocation } from '@/pages/main/massager/types';
import { massagerDetailFormSchema, type MassagerDetailFormData } from '@/schemas/massagerDetailSchema';
import { useDetailModalFormGuard } from '@/hooks/main/useMassagerPageFormGuard';
import { useMainMassagerUpdate } from '@/hooks/main/useMainMassager';
import {
  getMassagerDetailDefaultValues,
  mapMassagerDetailFormToUpdateRequest,
} from '@/utils/massagerDetailModal';

const FOCUS_CONSENT_FIELD_DELAY_MS = 100;

function formsEqual(a: MassagerDetailFormData, b: MassagerDetailFormData): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** After entering edit mode, focus the consent alert sub-field that matters for the current alert type. */
function focusConsentSubField(
  getValues: UseFormReturn<MassagerDetailFormData>['getValues'],
  setFocus: UseFormReturn<MassagerDetailFormData>['setFocus']
): void {
  const consentVal = getValues('alertType');
  if (consentVal === 2) setFocus('alertDay');
  else if (consentVal === 3 || consentVal == null) setFocus('alertMonth');
}

export interface UseMassagerDetailModalFormParams {
  massager: MassagerClinicLocation | null;
  isOpen: boolean;
  checkinEnabled: boolean;
  onClose: () => void;
}

export interface UseMassagerDetailModalFormResult {
  form: UseFormReturn<MassagerDetailFormData>;
  passwordVisible: boolean;
  setPasswordVisible: Dispatch<SetStateAction<boolean>>;
  isEditMode: boolean;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
  updateLoading: boolean;
  hasFormChanged: boolean;
  handleClose: () => void;
  handleCancelEdit: () => void;
  onSubmit: (formValues: MassagerDetailFormData) => void;
}

export function useMassagerDetailModalForm({
  massager,
  isOpen,
  checkinEnabled,
  onClose,
}: UseMassagerDetailModalFormParams): UseMassagerDetailModalFormResult {
  const { updateMutation, isLoading: updateLoading } = useMainMassagerUpdate();
  const { setFormChanged, confirmBeforeAction } = useDetailModalFormGuard();

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialData, setInitialData] = useState<MassagerDetailFormData>(() =>
    getMassagerDetailDefaultValues(massager)
  );

  const form = useTrimmedForm<MassagerDetailFormData>({
    resolver: zodResolver(massagerDetailFormSchema),
    defaultValues: getMassagerDetailDefaultValues(massager),
    mode: 'all',
  });

  const { reset, watch, getValues, setFocus } = form;
  const watchedValues = watch();
  const hasFormChanged = useMemo(
    () => !formsEqual(watchedValues, initialData),
    [watchedValues, initialData]
  );

  // Reset snapshot + form when modal opens/closes, row changes, or detail is still fetching (no row yet)
  useEffect(() => {
    if (!isOpen) {
      const empty = getMassagerDetailDefaultValues(null);
      reset(empty);
      setInitialData(empty);
      setIsEditMode(false);
      return;
    }
    if (massager) {
      const vals = getMassagerDetailDefaultValues(massager);
      reset(vals);
      setInitialData(vals);
      setIsEditMode(false);
      return;
    }
    const empty = getMassagerDetailDefaultValues(null);
    reset(empty);
    setInitialData(empty);
    setIsEditMode(false);
  }, [isOpen, massager, reset]);

  // Route-level / page guard: only flag dirty while detail modal is open and in edit mode
  useEffect(() => {
    setFormChanged(isOpen && isEditMode && hasFormChanged);
  }, [hasFormChanged, isOpen, isEditMode, setFormChanged]);

  // Check-in block: move focus to day/month input when editing consent settings
  useEffect(() => {
    if (!isEditMode || !checkinEnabled) return;
    const timerId = window.setTimeout(() => {
      focusConsentSubField(getValues, setFocus);
    }, FOCUS_CONSENT_FIELD_DELAY_MS);
    return () => window.clearTimeout(timerId);
  }, [isEditMode, checkinEnabled, getValues, setFocus]);

  const handleClose = useCallback(() => {
    if (hasFormChanged && isEditMode) {
      confirmBeforeAction(onClose);
      return;
    }
    onClose();
  }, [hasFormChanged, isEditMode, confirmBeforeAction, onClose]);

  const handleCancelEdit = useCallback(() => {
    const exitEdit = () => {
      reset(initialData);
      setIsEditMode(false);
    };
    if (hasFormChanged) {
      confirmBeforeAction(exitEdit);
      return;
    }
    exitEdit();
  }, [hasFormChanged, confirmBeforeAction, reset, initialData]);

  const onSubmit = useCallback(
    (formValues: MassagerDetailFormData) => {
      if (!massager) return;
      const payload = mapMassagerDetailFormToUpdateRequest(formValues, checkinEnabled);
      void updateMutation({ id: massager.id, data: payload }).then(() => {
        setFormChanged(false);
        setIsEditMode(false);
      });
    },
    [massager, checkinEnabled, updateMutation, setFormChanged]
  );

  return {
    form,
    passwordVisible,
    setPasswordVisible,
    isEditMode,
    setIsEditMode,
    updateLoading,
    hasFormChanged,
    handleClose,
    handleCancelEdit,
    onSubmit,
  };
}
